import type { CSSProperties } from 'react'
import { Toaster as SonnerToaster, toast } from 'sonner'

/**
 * Opaque equivalent of `--seed-light` (which is `rgba(74, 14, 17, 0.08)`).
 * A floating toast has no opaque layer of its own behind it, so the 8%-alpha
 * seed tint let the page content read straight through the toast — on mobile
 * the toast lands on top of the PageHeader and the text became unreadable.
 * Mixing the same 8% seed into `--surface-primary` keeps the identical visual
 * while making the surface opaque.
 */
const TOAST_SURFACE = 'color-mix(in srgb, var(--seed-base) 8%, var(--surface-primary))'

/** Shared Housien-branded surface for every toast type. */
export const BRAND_TOAST =
  'bg-[color:color-mix(in_srgb,var(--seed-base)_8%,var(--surface-primary))] border-[var(--seed-base)] text-[var(--seed-base)]'

const TOASTER_BRAND_VARS = {
  pointerEvents: 'auto',
  '--normal-bg': TOAST_SURFACE,
  '--normal-border': 'var(--seed-base)',
  '--normal-text': 'var(--seed-base)',
  '--success-bg': TOAST_SURFACE,
  '--success-border': 'var(--seed-base)',
  '--success-text': 'var(--seed-base)',
  '--error-bg': TOAST_SURFACE,
  '--error-border': 'var(--seed-base)',
  '--error-text': 'var(--seed-base)',
  '--warning-bg': TOAST_SURFACE,
  '--warning-border': 'var(--seed-base)',
  '--warning-text': 'var(--seed-base)',
  '--info-bg': TOAST_SURFACE,
  '--info-border': 'var(--seed-base)',
  '--info-text': 'var(--seed-base)',
} as CSSProperties

/**
 * Sonner-based toast container for the app root.
 * Mount this once in the provider tree; use the `toast` export for imperative calls.
 * All toast types use Housien seed (brand red) — no generic green/yellow/blue.
 * `expand` keeps successive toasts stacked with gap instead of piled/overlapping.
 *
 * @returns {JSX.Element} Sonner toaster component.
 */
export function SonnerProvider() {
  return (
    <SonnerToaster
      position="top-right"
      theme="light"
      expand
      gap={12}
      mobileOffset={{ top: 'calc(var(--header-height) + 16px)' }}
      style={TOASTER_BRAND_VARS}
      toastOptions={{
        classNames: {
          toast: `${BRAND_TOAST} shadow-[var(--shadow-lg)] rounded-(--radius-md)`,
          title: 'font-semibold text-sm text-[var(--seed-base)]',
          description: 'text-sm text-[var(--seed-base)] opacity-80',
          actionButton: 'bg-[var(--seed-base)] text-[var(--surface-primary)]',
          cancelButton: 'bg-[var(--surface-secondary)] text-[var(--text-secondary)]',
          closeButton: 'bg-[var(--surface-primary)] text-[var(--seed-base)] border-[var(--seed-base)]',
          error: BRAND_TOAST,
          success: BRAND_TOAST,
          warning: BRAND_TOAST,
          info: BRAND_TOAST,
        },
      }}
      closeButton
    />
  )
}

/**
 * Sonner toast helpers matching the existing useToast interface.
 *
 * @returns {{ showSuccess: Function, showError: Function, showWarning: Function, showInfo: Function }} Toast methods.
 */
export function useSonnerToast() {
  return {
    showSuccess: (message: string) => toast.success(message),
    showError: (message: string) => toast.error(message),
    showWarning: (message: string) => toast.warning(message),
    showInfo: (message: string) => toast.info(message),
  }
}

export { toast }
